import type { SetStateAction } from "react";
import type {
  ReploElementMetadata,
  ReploPartialElement,
} from "schemas/generated/element";

import * as React from "react";

import { skipToken } from "@tanstack/react-query";
import { successToast } from "@v1/components/designSystem/alert/Toast";
import { useElementValidation } from "@v1/hooks/element";
import { useDraftElementMetadata } from "@v1/hooks/useDraftElementMetadata";
import { selectElementIsLoading } from "@v1/reducers/core-reducer";
import { useEditorSelector } from "@v1/store/store";
import { sanitizeElementName } from "@v1/utils/element";
import { analytics } from "@v2/features/infra/analytics/tracking";
import { elementRouter, elementUtils } from "@v2/trpc/routes/element";

/**
 * This hook handles update, validation and the state of a Replo element.
 */
export function useEditableElement({
  element: initialElement,
  elementId: initialElementId,
}: {
  element?: ReploElementMetadata;
  elementId?: string;
}) {
  const draftElementMetadata = useDraftElementMetadata();
  const { data: initialElementMetadata } =
    elementRouter.getElementMetadataById.useQuery(
      initialElement?.id ?? initialElementId ?? skipToken,
      {
        // NOTE (Ben O., 2025-02-12): Whenever we call updateElementMetadata,
        // we should be voiding this trpc call but adding this here to ensure
        // that the element metadata is refetched when the element is updated always
        refetchOnMount: true,
      },
    );
  const originalElement = initialElementMetadata ?? draftElementMetadata;

  const [element, setElement] = React.useState<
    ReploElementMetadata | undefined
  >(originalElement ?? undefined);

  React.useEffect(() => {
    if (!element && originalElement) {
      setElement(originalElement);
    }
  }, [element, originalElement]);

  React.useEffect(() => {
    if (initialElementMetadata) {
      setElement(initialElementMetadata);
    }
  }, [initialElementMetadata]);

  const validateElement = useElementValidation(element?.type);
  const isElementLoading = useEditorSelector(selectElementIsLoading);
  const [isUpdatingElement, setIsUpdatingElement] = React.useState(false);

  const updateElementMetadata = elementRouter.updateElementMetadata.useMutation(
    {
      onSuccess: () => {
        void elementUtils.getElementMetadataById.invalidate(element?.id);
        void elementUtils.listAllElementsMetadata.invalidate();
        successToast("Settings updated!");
      },
    },
  );

  const onChangeElement = React.useCallback(
    (value: SetStateAction<ReploPartialElement>) => {
      setElement((prev) => {
        if (!prev) {
          return prev;
        }
        return {
          ...prev,
          ...(typeof value === "function" ? value(prev) : value),
        };
      });
    },
    [],
  );

  const onUpdateElementMetadata = React.useCallback(
    async function onUpdateElementMetadataFn(element: ReploElementMetadata) {
      setIsUpdatingElement(true);
      analytics.logEvent("element.update", {
        type: element.type,
        elementId: element.id,
      });

      await updateElementMetadata
        .mutateAsync({
          element: {
            ...element,
            name: sanitizeElementName(element.name, element.type),
          },
        })
        .finally(() => {
          setIsUpdatingElement(false);
        });
    },
    [updateElementMetadata],
  );

  return {
    element: element,
    onChangeElement,
    updateElementMetadata: onUpdateElementMetadata,
    validateElement,
    isUpdatingElement: isElementLoading || isUpdatingElement,
  };
}
